import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { uploadMediaFile } from '@/hooks/useUpload';
import {
  createAccountType,
  listAccountTypes,
  toggleAccountType,
  updateAccountType,
  type AccountType,
  type AccountTypeInput,
} from '@/lib/accountTypes';
import {
  CAPACITY_OPTIONS,
  loadAccountCapabilities,
  refreshAccountCapabilities,
} from '@/lib/accountCapabilities';

const EMPTY_FORM: AccountTypeInput = {
  nom: '',
  statustypecompte: 0,
  page: 'marketplace',
  image: '',
  ordre: 0,
  actif: true,
  capacites: '',
  parent_id: null,
};

const PAGE_OPTIONS = [
  { value: '', label: 'Aucune page dédiée' },
  { value: 'marketplace', label: 'Place de marché (marketplace)' },
  { value: 'becomeseller', label: 'Devenir vendeur (becomeseller)' },
];

function pageLabel(page: string): string {
  return PAGE_OPTIONS.find((p) => p.value === page)?.label || page || '—';
}

function capacityLabel(value: string | null | undefined): string {
  return CAPACITY_OPTIONS.find((c) => c.value === value)?.label || 'Adaptatif';
}

export default function SuperAdminAccountTypesPage() {
  const { user } = useAuth();
  const [types, setTypes] = useState<AccountType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AccountType | null>(null);
  const [form, setForm] = useState<AccountTypeInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadImageError, setUploadImageError] = useState('');
  const imageInputRef = useRef<HTMLInputElement>(null);

  const identity = user ? { userId: user.id, email: user.email } : null;

  const load = useCallback(async () => {
    if (!identity) return;
    setLoading(true);
    setError('');
    try {
      const list = await listAccountTypes(identity);
      setTypes(list);
      // Rafraîchit le cache des capacités pour reconnaître tout nouveau type.
      refreshAccountCapabilities();
      void loadAccountCapabilities();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les types de compte.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const openCreate = (parentId: number | null = null) => {
    setEditing(null);
    const parent = parentId ? types.find((t) => t.id === parentId) : null;
    setForm({
      ...EMPTY_FORM,
      ordre: types.length + 1,
      parent_id: parentId,
      statustypecompte: parent ? parent.statustypecompte : 0,
      capacites: parent?.capacites || '',
      page: parent?.page || '',
    });
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (t: AccountType) => {
    setEditing(t);
    setForm({
      nom: t.nom,
      statustypecompte: t.statustypecompte,
      page: t.page || '',
      image: t.image || '',
      ordre: t.ordre || 0,
      actif: t.actif !== false,
      capacites: t.capacites || '',
      parent_id: t.parent_id ?? null,
    });
    setFormError('');
    setFormOpen(true);
  };

  const handleToggle = async (t: AccountType) => {
    if (!identity) return;
    setTogglingId(t.id);
    // Optimiste : on inverse immédiatement l'affichage.
    setTypes((prev) => prev.map((x) => (x.id === t.id ? { ...x, actif: !(x.actif !== false) } : x)));
    try {
      const next = !(t.actif !== false);
      await toggleAccountType(identity, t.id, next);
    } catch {
      // En cas d'échec, on revient à l'état précédent.
      setTypes((prev) => prev.map((x) => (x.id === t.id ? { ...x, actif: t.actif !== false } : x)));
    } finally {
      setTogglingId(null);
    }
  };

  // Téléverse une image depuis l'appareil et l'assigne au type de compte.
  const handleImageFile = async (file: File | null) => {
    if (!file) return;
    setUploadImageError('');
    setUploadingImage(true);
    try {
      const url = await uploadMediaFile(file, 'account-types');
      setForm((prev) => ({ ...prev, image: url }));
    } catch (e) {
      setUploadImageError(e instanceof Error ? e.message : 'Échec de l\u2019envoi de l\u2019image.');
    } finally {
      setUploadingImage(false);
    }
  };

  const save = async () => {
    if (!identity) return;
    if (!form.nom.trim()) {
      setFormError('Le nom du type de compte est obligatoire.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload: AccountTypeInput = {
        nom: form.nom.trim(),
        statustypecompte: Number(form.statustypecompte) === 1 ? 1 : 0,
        page: form.page || '',
        image: form.image.trim(),
        ordre: Number(form.ordre) || 0,
        actif: form.actif !== false,
        capacites: form.capacites || '',
        parent_id: form.parent_id ?? null,
      };
      if (editing) await updateAccountType(identity, editing.id, payload);
      else await createAccountType(identity, payload);
      setFormOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Échec de l\u2019enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const merchants = types.filter((t) => t.statustypecompte !== 1).length;
  const clients = types.filter((t) => t.statustypecompte === 1).length;
  const visibleAtSignup = types.filter((t) => t.actif !== false && !t.parent_id).length;
  const hiddenAtSignup = types.length - visibleAtSignup;
  const parents = types.filter((t) => !t.parent_id);
  const subTypesCount = types.filter((t) => !!t.parent_id).length;

  const parentName = (id: number | null | undefined): string => {
    if (!id) return '';
    const parent = types.find((t) => t.id === Number(id));
    return parent ? parent.nom : `#${id}`;
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Types de compte</h2>
          <p className="text-sm text-foreground-500 mt-1">
            {types.length} types · {subTypesCount} sous-type(s) · {merchants} commerçant(s) · {clients} client(s) · {visibleAtSignup} visible(s) à l&apos;inscription
            {hiddenAtSignup > 0 ? ` · ${hiddenAtSignup} masqué(s)` : ''}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
        >
          <i className="ri-add-line"></i>
          Nouveau type de compte
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : types.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-price-tag-3-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600">Aucun type de compte. Créez le premier.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {types.map((t) => {
            const isClient = t.statustypecompte === 1;
            const isActive = t.actif !== false;
            return (
              <div
                key={t.id}
                className={`bg-background-50 border rounded-lg p-5 transition-colors ${
                  isActive ? 'border-background-200/70' : 'border-background-200/70 opacity-60'
                }`}
              >
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isClient ? 'bg-secondary-100 text-secondary-700' : 'bg-primary-100 text-primary-700'
                    }`}>
                      <i className={isClient ? 'ri-user-line text-lg' : 'ri-store-2-line text-lg'}></i>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-950 truncate">{t.nom}</h3>
                      <span className="text-[11px] text-foreground-400">
                        {t.parent_id ? `Sous-type de ${parentName(t.parent_id)}` : `ID #${t.id}`}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                      isClient ? 'bg-secondary-100 text-secondary-900' : 'bg-primary-100 text-primary-900'
                    }`}>
                      {isClient ? 'Client' : 'Commerçant'}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap inline-flex items-center gap-1 ${
                      isActive ? 'bg-accent-100 text-accent-900' : 'bg-foreground-100 text-foreground-600'
                    }`}>
                      <i className={isActive ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                      {isActive ? 'Visible à l\u2019inscription' : 'Masqué à l\u2019inscription'}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap inline-flex items-center gap-1 bg-background-100 text-foreground-600">
                      <i className="ri-stack-line"></i>
                      {capacityLabel(t.capacites)}
                    </span>
                  </div>
                </div>

                <div className="text-xs text-foreground-500 space-y-1 mb-4">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-link text-foreground-400"></i>
                    <span className="truncate">{pageLabel(t.page)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-image-line text-foreground-400"></i>
                    <span className="truncate">{t.image || 'Aucune image'}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEdit(t)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors"
                  >
                    <i className="ri-pencil-line"></i>
                    Modifier
                  </button>
                  {!t.parent_id && (
                    <button
                      onClick={() => openCreate(t.id)}
                      title={`Créer un sous-type de ${t.nom}`}
                      className="flex items-center justify-center gap-1 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors"
                    >
                      <i className="ri-add-line"></i>
                      Sous-type
                    </button>
                  )}
                  <button
                    onClick={() => handleToggle(t)}
                    disabled={togglingId === t.id}
                    title={isActive ? 'Désactiver (masqué à l\u2019inscription)' : 'Activer (visible à l\u2019inscription)'}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 ${
                      isActive
                        ? 'bg-accent-100 text-accent-900 hover:bg-accent-200'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                    }`}
                  >
                    {togglingId === t.id ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className={isActive ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'}></i>
                    )}
                    {isActive ? 'Masquer' : 'Afficher'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setFormOpen(false)}>
          <div className="bg-background-50 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
              <h3 className="text-base font-semibold text-foreground-950">
                {editing ? 'Modifier le type de compte' : 'Nouveau type de compte'}
              </h3>
              <button
                onClick={() => setFormOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Nom</label>
                <input
                  type="text"
                  value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                  placeholder="ex. Restauration"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Sous-type de</label>
                <select
                  value={form.parent_id ? String(form.parent_id) : ''}
                  onChange={(e) => {
                    const parentId = e.target.value ? Number(e.target.value) : null;
                    const parent = parentId ? types.find((t) => t.id === parentId) : null;
                    setForm({
                      ...form,
                      parent_id: parentId,
                      statustypecompte: parent ? parent.statustypecompte : form.statustypecompte,
                      capacites: parent && !form.capacites ? (parent.capacites || '') : form.capacites,
                    });
                  }}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Type principal (aucun parent)</option>
                  {parents
                    .filter((p) => !editing || p.id !== editing.id)
                    .map((p) => (
                      <option key={p.id} value={p.id}>{p.nom}</option>
                    ))}
                </select>
                <p className="mt-1 text-[11px] text-foreground-400">
                  Ex. « Boutique » → sous-types « Électronique », « Mode », « Alimentaire ».
                  Un sous-type n&apos;apparaît pas dans le formulaire d&apos;inscription.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Catégorie</label>
                <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5">
                  {[
                    { value: 0, label: 'Commerçant' },
                    { value: 1, label: 'Client' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setForm({ ...form, statustypecompte: opt.value })}
                      className={`flex-1 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                        form.statustypecompte === opt.value
                          ? 'bg-background-50 text-foreground-900 shadow-sm'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Page dédiée</label>
                <select
                  value={form.page}
                  onChange={(e) => setForm({ ...form, page: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {PAGE_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Capacité de boutique</label>
                <select
                  value={form.capacites}
                  onChange={(e) => setForm({ ...form, capacites: e.target.value })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Adaptatif (selon le contenu)</option>
                  {CAPACITY_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label} — {c.hint}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-foreground-400">
                  Détermine ce que le site public affiche (produits, services, marketplace…).
                  Un type sans capacité reste adaptatif.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Image du type de compte</label>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-background-100 border border-background-200/70 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {form.image ? (
                      <img src={form.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <i className="ri-image-line text-xl text-foreground-400"></i>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        disabled={uploadingImage}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors"
                      >
                        {uploadingImage ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
                        {uploadingImage ? 'Envoi...' : 'Téléverser une image'}
                      </button>
                      {form.image && (
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, image: '' })}
                          className="flex items-center gap-1 px-3 py-2 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors"
                        >
                          <i className="ri-delete-bin-line"></i>Retirer
                        </button>
                      )}
                    </div>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => { handleImageFile(e.target.files?.[0] ?? null); e.target.value = ''; }}
                    />
                    <input
                      type="text"
                      value={form.image}
                      onChange={(e) => setForm({ ...form, image: e.target.value })}
                      placeholder="… ou collez une URL / un nom de fichier"
                      className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                    />
                    {uploadImageError && (
                      <p className="text-[11px] text-red-600 flex items-center gap-1"><i className="ri-error-warning-line"></i>{uploadImageError}</p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Ordre</label>
                <input
                  type="number"
                  value={form.ordre}
                  onChange={(e) => setForm({ ...form, ordre: Number(e.target.value) })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-background-100 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-foreground-900">Visible à l&apos;inscription</div>
                  <div className="text-[11px] text-foreground-500">
                    {form.actif !== false ? 'Ce type apparaît dans le formulaire d\u2019inscription.' : 'Ce type est masqué à l\u2019inscription.'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, actif: form.actif === false })}
                  className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors cursor-pointer flex-shrink-0 ${
                    form.actif !== false ? 'bg-accent-500 justify-end' : 'bg-background-300/70 justify-start'
                  }`}
                  aria-label="Activer ou désactiver le type"
                >
                  <span className="w-5 h-5 rounded-full bg-background-50 block"></span>
                </button>
              </div>

              {formError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-center gap-1.5">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </p>
              )}
            </div>

            <div className="flex gap-3 px-5 py-4 border-t border-background-200/70">
              <button
                onClick={() => setFormOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                {editing ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}